import { Component, ElementRef, ViewChild } from '@angular/core'
import { Store } from '@ngrx/store'
import { MarkerDimension, PropertySummary, SegObjectPropertyMap } from '../../../state/case/case.state'
import { plotDataActions } from '../../../state/plot-data/plot-data.actions'
import { PlotAxis } from '../../../state/plot-data/plot-data.state'
import {
    selectMarkerX,
    selectMarkerY,
    selectMarkerColor,
} from '../../../state/plot-data/plot-data.selectors'
import {
    selectHasMarkers,
    selectSharedPropertyNames,
    selectPropertyCatalogByMapId,
    selectLoadedSegObjectPropertyMaps,
    selectMarkerDimensionsByMapId,
} from '../../../state/case/case.selectors'
import { DownloadService } from '../../../shared/services/download.service'
import { HttpErrorResponse } from '@angular/common/http'
import { ToasterService } from '../../../shared/services/toaster.service'
import { BehaviorSubject, combineLatest, map, take } from 'rxjs'
import {
    selectSavedFreeSelectionNames,
    selectSavedHierarchicalSelectionNodeItems,
    selectSavedUnsupervisedSelectionNodeItems,
    selectSelectionState,
    selectDisplayedSelectionsOnScatterPlot,
    selectHierarchicalSelectionNodeItem,
} from '../../../state/selection/selection.selectors'
import { selectionActions } from 'src/app/state/selection/selection.actions'
import { SelectionBase, SelectionType } from 'src/app/state/selection/selection.state'
import { MarkerCatalogService } from '../../../shared/services/marker-catalog.service'
import {
    createMappingMarkerDimension,
    isMappingProperty,
    mappingPropertySummary,
    MAPPING_PROPERTY_NAME,
} from '../../../state/plot-data/mapping-property'
import { filterSharedPropertyOptions } from '../../../shared/utils/property-catalog-utils'

interface TreeNode {
    id: string
    show: boolean
    selected: boolean
    nodes: TreeNode[]
    isTopLevel?: boolean
}

const NONE_COLOR_OPTION: PropertySummary = { name: 'None', lower_bound: 0, upper_bound: 0 }
const MAX_FILTERED_OPTIONS = 50
const MORE_RESULTS_INDICATOR = '…'

interface FilteredMarkerOptions {
    options: PropertySummary[]
    hasMore: boolean
}

@Component({
    selector: 'app-plot-data-selection',
    templateUrl: './plot-data-selection.component.html',
    styleUrls: ['./plot-data-selection.component.scss'],
})
export class PlotDataSelectionComponent {
    readonly markerXName$ = this.store
        .select(selectMarkerX)
        .pipe(map(marker => marker?.name ?? null))
    readonly markerYName$ = this.store
        .select(selectMarkerY)
        .pipe(map(marker => marker?.name ?? null))
    readonly markerColorName$ = this.store.select(selectMarkerColor).pipe(
        map(marker => (marker ? marker.name : 'None'))
    )
    readonly hasMarkers$ = this.store.select(selectHasMarkers)
    readonly loadedSegObjectPropertyMaps$ = this.store.select(selectLoadedSegObjectPropertyMaps)
    readonly selectionState$ = this.store.select(selectSelectionState)

    private readonly axisSearchX$ = new BehaviorSubject('')
    private readonly axisSearchY$ = new BehaviorSubject('')
    private readonly axisSearchColor$ = new BehaviorSubject('')

    readonly filteredXMarkers$ = combineLatest({
        names: this.store.select(selectSharedPropertyNames),
        byMapId: this.store.select(selectPropertyCatalogByMapId),
        loadedMaps: this.store.select(selectLoadedSegObjectPropertyMaps),
        search: this.axisSearchX$,
    }).pipe(
        map(({ names, byMapId, loadedMaps, search }) =>
            this.filterSharedNames(names, search, byMapId, loadedMaps)
        )
    )

    readonly filteredYMarkers$ = combineLatest({
        names: this.store.select(selectSharedPropertyNames),
        byMapId: this.store.select(selectPropertyCatalogByMapId),
        loadedMaps: this.store.select(selectLoadedSegObjectPropertyMaps),
        search: this.axisSearchY$,
    }).pipe(
        map(({ names, byMapId, loadedMaps, search }) =>
            this.filterSharedNames(names, search, byMapId, loadedMaps)
        )
    )

    readonly filteredColorMarkers$ = combineLatest({
        names: this.store.select(selectSharedPropertyNames),
        byMapId: this.store.select(selectPropertyCatalogByMapId),
        search: this.axisSearchColor$,
        loadedMaps: this.store.select(selectLoadedSegObjectPropertyMaps),
    }).pipe(
        map(({ names, byMapId, search, loadedMaps }) => {
            const colorNames =
                loadedMaps.length > 1 ? [MAPPING_PROPERTY_NAME, ...names] : names
            const filtered = this.filterSharedNames(colorNames, search, byMapId, loadedMaps)
            return {
                options: [NONE_COLOR_OPTION, ...filtered.options],
                hasMore: filtered.hasMore,
            }
        })
    )

    readonly colorAxisActive$ = this.markerColorName$.pipe(
        map(name => !!name && name !== 'None')
    )

    private selections$: BehaviorSubject<{ name: string; level: number }[]> = new BehaviorSubject<
        { name: string; level: number }[]
    >([])
    readonly savedFreeSelectionNames$ = this.store.select(selectSavedFreeSelectionNames)
    readonly savedHierarchicalSelectionNodeItems$ = this.store.select(
        selectSavedHierarchicalSelectionNodeItems
    )
    readonly savedUnsupervisedSelectionNodeItems$ = this.store.select(
        selectSavedUnsupervisedSelectionNodeItems
    )
    readonly displayedSelections$ = this.store.select(selectDisplayedSelectionsOnScatterPlot)

    selectionType: SelectionType = SelectionType.Free

    selectedBoxes: string[] = []
    selectionNodes: TreeNode[] = [
        {
            id: 'Second Tree Root',
            show: false,
            selected: false,
            isTopLevel: true,
            nodes: [{ id: 'selections', show: false, selected: false, nodes: [] }],
        },
    ]
    showSelectionNode: boolean = false
    selectedSelectionName = ''

    @ViewChild('xAxisCombo', { read: ElementRef })
    private xAxisCombo?: ElementRef<HTMLElement>
    @ViewChild('yAxisCombo', { read: ElementRef })
    private yAxisCombo?: ElementRef<HTMLElement>
    @ViewChild('colorAxisCombo', { read: ElementRef })
    private colorAxisCombo?: ElementRef<HTMLElement>

    private xAxisSelectedWhileOpen = false
    private yAxisSelectedWhileOpen = false
    private colorAxisSelectedWhileOpen = false

    constructor(
        private readonly store: Store,
        private readonly toaster: ToasterService,
        private readonly downloader: DownloadService,
        private readonly markerCatalog: MarkerCatalogService
    ) {
        this.selectionState$.pipe().subscribe(selectionState => {
            if (this.selectionType !== selectionState.type) {
                this.selectionType = selectionState.type
                this.selectedBoxes = []
            }

            if (selectionState.type === SelectionType.Hierarchical) {
                this.selectedSelectionName =
                    selectionState.hierarchicalSelection.selectedHierarchyName!
                this.savedHierarchicalSelectionNodeItems$.pipe(take(1)).subscribe(items => {
                    this.selections$.next(
                        items.map(item => ({ name: item.name!, level: item.level }))
                    )
                })
            } else if (selectionState.type === SelectionType.Unsupervised) {
                this.selectedSelectionName =
                    selectionState.unsupervisedSelection.selectedUnsupervisedName!
                this.savedUnsupervisedSelectionNodeItems$.pipe(take(1)).subscribe(items => {
                    this.selections$.next(
                        items.map(item => ({ name: item.name!, level: item.level }))
                    )
                })
            }

            this.selections$.subscribe(items => {
                const selections = items.map(item => ({
                    id: item.name,
                    level: item.level,
                    show: false,
                    selected: [...selectionState.displayedOnScatterPlot]!.find(
                        (s: SelectionBase) => s.name === item.name
                    )
                        ? true
                        : false,
                    nodes: [],
                }))
                this.selectionNodes = [
                    {
                        id: 'Displayed selections',
                        show: this.showSelectionNode,
                        selected: false,
                        nodes: selections,
                        isTopLevel: true,
                    },
                ]
            })
        })

        this.displayedSelections$.pipe().subscribe(currentSelections => {
            this.selectedBoxes = currentSelections.map(s => s.name!)
        })
    }

    onXAxisSearch(search: string | unknown): void {
        this.axisSearchX$.next(this.coerceSearchTerm(search))
    }

    onYAxisSearch(search: string | unknown): void {
        this.axisSearchY$.next(this.coerceSearchTerm(search))
    }

    onColorAxisSearch(search: string | unknown): void {
        this.axisSearchColor$.next(this.coerceSearchTerm(search))
    }

    onXAxisOpenChange(open: boolean): void {
        if (open) {
            this.xAxisSelectedWhileOpen = false
            this.axisSearchX$.next('')
            return
        }
        this.axisSearchX$.next('')
        // Wait a tick so a real option selection can set xAxisSelectedWhileOpen first.
        setTimeout(() => this.restoreComboboxInputIfUnchanged(this.xAxisCombo, this.markerXName$, this.xAxisSelectedWhileOpen))
    }

    onYAxisOpenChange(open: boolean): void {
        if (open) {
            this.yAxisSelectedWhileOpen = false
            this.axisSearchY$.next('')
            return
        }
        this.axisSearchY$.next('')
        setTimeout(() => this.restoreComboboxInputIfUnchanged(this.yAxisCombo, this.markerYName$, this.yAxisSelectedWhileOpen))
    }

    onColorAxisOpenChange(open: boolean): void {
        if (open) {
            this.colorAxisSelectedWhileOpen = false
            this.axisSearchColor$.next('')
            return
        }
        this.axisSearchColor$.next('')
        setTimeout(() =>
            this.restoreComboboxInputIfUnchanged(
                this.colorAxisCombo,
                this.markerColorName$,
                this.colorAxisSelectedWhileOpen
            )
        )
    }

    /**
     * If the user typed a filter and closed without picking an option, put the
     * selected axis name back in the input. Does not touch ngModel (that broke selection).
     */
    private restoreComboboxInputIfUnchanged(
        combo: ElementRef<HTMLElement> | undefined,
        selected$: typeof this.markerXName$,
        selectedWhileOpen: boolean
    ): void {
        if (selectedWhileOpen) return
        selected$.pipe(take(1)).subscribe(selected => {
            const input = combo?.nativeElement?.querySelector('input')
            if (!input) return
            const next = selected ?? ''
            if (input.value !== next) {
                input.value = next
            }
        })
    }

    setDataXAxis(markerName: string | null): void {
        if (!markerName || markerName === MORE_RESULTS_INDICATOR) return
        this.xAxisSelectedWhileOpen = true
        this.store
            .select(selectMarkerX)
            .pipe(take(1))
            .subscribe(markerX => {
                if (markerX?.name === markerName) return
                this.setDataAxis(markerName, PlotAxis.X)
            })
    }

    setDataYAxis(markerName: string | null): void {
        if (!markerName || markerName === MORE_RESULTS_INDICATOR) return
        this.yAxisSelectedWhileOpen = true
        this.store
            .select(selectMarkerY)
            .pipe(take(1))
            .subscribe(markerY => {
                if (markerY?.name === markerName) return
                this.setDataAxis(markerName, PlotAxis.Y)
            })
    }

    setDataColorAxis(markerName: string | null): void {
        if (markerName === MORE_RESULTS_INDICATOR) return
        // Clarity may emit null on dismiss; only treat an explicit pick as a selection.
        if (!markerName) return

        this.colorAxisSelectedWhileOpen = true

        if (markerName === 'None') {
            this.store.dispatch(
                plotDataActions.loadDataForAxis({
                    marker: null,
                    axis: PlotAxis.COLOR,
                })
            )
            this.store
                .select(selectHierarchicalSelectionNodeItem)
                .pipe(take(1))
                .subscribe(item => {
                    this.store.dispatch(
                        selectionActions.setHierarchicalSelectionNode({ name: item.name! })
                    )
                })
            return
        }

        this.setDataAxis(markerName, PlotAxis.COLOR)
        const tree = this.selectionNodes[0]
        this.selectionNodes = [{ ...tree, show: false }]
    }

    private setDataAxis(markerName: string, axis: PlotAxis): void {
        if (axis === PlotAxis.COLOR) {
            this.store
                .select(selectLoadedSegObjectPropertyMaps)
                .pipe(take(1))
                .subscribe(loadedMaps => {
                    if (markerName === 'None' || !markerName) {
                        this.store.dispatch(
                            plotDataActions.loadDataForAxis({
                                marker: null,
                                axis: PlotAxis.COLOR,
                            })
                        )
                        return
                    }
                    if (isMappingProperty(markerName)) {
                        if (loadedMaps.length <= 1) {
                            this.toaster.error(
                                '"intensity map" is only available when multiple intensity maps are selected.',
                                'Unsupported'
                            )
                            return
                        }
                        const fullMarker = createMappingMarkerDimension(
                            loadedMaps.length,
                            loadedMaps[0]
                        )
                        this.store.dispatch(
                            plotDataActions.loadDataForAxis({
                                marker: fullMarker,
                                axis: PlotAxis.COLOR,
                            })
                        )
                        return
                    }
                    const fullMarker = this.resolveMarker({
                        name: markerName,
                        lower_bound: 0,
                        upper_bound: 0,
                    })
                    if (!fullMarker) return
                    this.store.dispatch(
                        plotDataActions.loadDataForAxis({
                            marker: fullMarker,
                            axis: PlotAxis.COLOR,
                        })
                    )
                })
            return
        }

        combineLatest([
            this.store.select(selectMarkerX),
            this.store.select(selectMarkerY),
            this.store.select(selectLoadedSegObjectPropertyMaps),
            this.store.select(selectMarkerDimensionsByMapId),
        ])
            .pipe(take(1))
            .subscribe(([markerX, markerY, loadedMaps, markerDimensionsByMapId]) => {
                if (isMappingProperty(markerName)) {
                    this.toaster.error(
                        '"intensity map" can only be used for the color axis.',
                        'Unsupported'
                    )
                    return
                }
                const fullMarker = this.resolveMarkerForMaps(
                    markerName,
                    loadedMaps,
                    markerDimensionsByMapId
                )
                if (!fullMarker) return

                const nextMarkerX = axis === PlotAxis.X ? fullMarker : markerX
                const nextMarkerY = axis === PlotAxis.Y ? fullMarker : markerY
                if (!nextMarkerX || !nextMarkerY) return

                // Single-map: always reload both axes together. Loading one axis via
                // loadDataForAxis assumes identical segment-ID order across property
                // bins, which is not guaranteed (e.g. switching to x/y).
                if (loadedMaps.length <= 1) {
                    this.store.dispatch(
                        plotDataActions.loadDataBothAxes({
                            markerXAxis: nextMarkerX,
                            markerYAxis: nextMarkerY,
                        })
                    )
                    return
                }

                console.log('[PlotData]', 'axis change → multi-map reload', {
                    axis,
                    markerName,
                    markerXName: nextMarkerX.name,
                    markerYName: nextMarkerY.name,
                    loadedMapCount: loadedMaps.length,
                })
                this.store.dispatch(
                    plotDataActions.syncMarkerAxes({
                        markerX: nextMarkerX,
                        markerY: nextMarkerY,
                    })
                )
                this.store.dispatch(
                    plotDataActions.loadMultiMappingPlotData({
                        markerXName: nextMarkerX.name,
                        markerYName: nextMarkerY.name,
                    })
                )
            })
    }

    private resolveMarkerForMaps(
        markerName: string,
        loadedMaps: SegObjectPropertyMap[],
        markerDimensionsByMapId: Record<number, MarkerDimension[]>
    ): MarkerDimension | null {
        if (isMappingProperty(markerName)) {
            this.toaster.error(
                '"intensity map" can only be used for the color axis.',
                'Unsupported'
            )
            return null
        }
        for (const map of loadedMaps) {
            const mapId = Number(map.id)
            const marker = markerDimensionsByMapId[mapId]?.find(m => m.name === markerName)
            if (marker) return marker
        }
        const fullMarker = this.markerCatalog.getFullMarker(markerName)
        if (!fullMarker) {
            this.toaster.error(`Could not load property "${markerName}".`, 'Error')
        }
        return fullMarker ?? null
    }

    toggleSelection(name: string): void {
        if (this.selectedBoxes.includes(name)) {
            this.selectedBoxes = this.selectedBoxes.filter(box => box !== name)
        } else {
            this.selectedBoxes = [...this.selectedBoxes, name]
        }

        this.store.dispatch(
            selectionActions.setVisibleSelectionsOnScatterPlot({
                selectionNames: this.selectedBoxes,
            })
        )
    }

    selectedBox(name: string): boolean {
        return this.selectedBoxes.includes(name)
    }

    selectSelection(node: TreeNode) {
        node.selected = !node.selected
        if (node.nodes.length > 0) {
            this.updateChildNodes(node.nodes, node.selected)
        }

        if (this.selectedBoxes.includes(node.id)) {
            this.selectedBoxes = this.selectedBoxes.filter(box => box !== node.id)
        } else {
            this.selectedBoxes = [...this.selectedBoxes, node.id]
        }
        this.store.dispatch(
            selectionActions.setVisibleSelectionsOnScatterPlot({
                selectionNames: this.selectedBoxes,
            })
        )
    }

    toggleNode(node: TreeNode) {
        node.show = !node.show
        if (node.isTopLevel) {
            this.showSelectionNode = node.show
        }
    }

    selectNode(node: TreeNode) {
        node.selected = !node.selected
        if (node.nodes.length > 0) {
            this.updateChildNodes(node.nodes, node.selected)
        }
    }

    private updateChildNodes(nodes: TreeNode[], selected: boolean) {
        nodes.forEach(node => {
            node.selected = selected
            if (node.nodes.length > 0) {
                this.updateChildNodes(node.nodes, selected)
            }
        })
    }

    downloadMarkers(): void {
        this.loadedSegObjectPropertyMaps$.pipe(take(1)).subscribe(maps => {
            this.downloader.downloadMarkers(maps).subscribe({
                error: (err: HttpErrorResponse) => {
                    this.toaster.httpError(err, 'Failed to download markers.')
                },
            })
        })
    }

    hasChildren(node: TreeNode): boolean {
        return node.nodes && node.nodes.length > 0
    }

    getSelectionLabel(node: { id: string; level: number }): string {
        return (
            '&nbsp;'.repeat((node.level - 1) * 3) +
            (node.level > 1 ? '\u21B3 ' : '') +
            node.id +
            (this.selectedSelectionName === node.id ? ' (selected)' : '')
        )
    }

    private filterSharedNames(
        names: string[],
        search: string,
        byMapId: Record<number, PropertySummary[]>,
        loadedMaps: SegObjectPropertyMap[]
    ): FilteredMarkerOptions {
        const mapCount = loadedMaps.length
        let catalog: PropertySummary[] | undefined
        for (const map of loadedMaps) {
            const c = byMapId[map.id]
            if (c?.length) {
                catalog = c
                break
            }
        }

        const resolveMapping = (name: string): PropertySummary | undefined =>
            isMappingProperty(name) ? mappingPropertySummary(mapCount) : undefined

        // Empty search: walk shared names vs sorted catalog (two-pointer), hydrate only up to limit.
        if (!search.trim()) {
            const options: PropertySummary[] = []
            let ci = 0
            const sorted = catalog ?? []
            for (let ni = 0; ni < names.length && options.length < MAX_FILTERED_OPTIONS; ni++) {
                const name = names[ni]
                const mapping = resolveMapping(name)
                if (mapping) {
                    options.push(mapping)
                    continue
                }
                while (ci < sorted.length && sorted[ci].name.localeCompare(name) < 0) {
                    ci++
                }
                if (ci < sorted.length && sorted[ci].name === name) {
                    options.push(sorted[ci])
                    ci++
                }
            }
            return { options, hasMore: names.length > MAX_FILTERED_OPTIONS }
        }

        const byName = catalog ? new Map(catalog.map(p => [p.name, p])) : undefined
        return filterSharedPropertyOptions(names, search, MAX_FILTERED_OPTIONS, name => {
            return resolveMapping(name) ?? byName?.get(name)
        })
    }

    private coerceSearchTerm(search: string | unknown): string {
        return typeof search === 'string' ? search : ''
    }

    private resolveMarker(marker: PropertySummary | MarkerDimension): MarkerDimension | null {
        if ('source_bin' in marker && marker.source_bin) {
            return marker as MarkerDimension
        }
        const fullMarker = this.markerCatalog.getFullMarker(marker.name)
        if (!fullMarker) {
            this.toaster.error(`Could not load property "${marker.name}".`, 'Error')
        }
        return fullMarker ?? null
    }
}
